<template>
  <div class="page-container">
    <!-- 查询条件 -->
    <div class="toolbar">
      <div class="filters">
        <el-select v-model="query.storeId" placeholder="全部粮库" clearable filterable reserve-keyword style="width: 180px">
          <el-option v-for="s in stores" :key="s.id" :label="s.storeName" :value="s.id" />
        </el-select>
        <el-select v-model="query.alarmType" placeholder="全部类型" clearable style="width: 140px">
          <el-option label="温度异常" :value="1" />
          <el-option label="湿度异常" :value="2" />
          <el-option label="设备故障" :value="3" />
          <el-option label="入侵报警" :value="4" />
        </el-select>
        <el-select v-model="query.status" placeholder="全部状态" clearable style="width: 140px">
          <el-option label="待处理" :value="0" />
          <el-option label="处理中" :value="1" />
          <el-option label="已处理" :value="2" />
        </el-select>
        <el-input v-model="query.keyword" placeholder="报警编号/内容" clearable style="width: 200px"
                  @keyup.enter="loadAlarms" />
        <el-date-picker v-model="query.dateRange" type="datetimerange" range-separator="至"
                        start-placeholder="开始时间" end-placeholder="结束时间" value-format="YYYY-MM-DD HH:mm:ss"
                        style="width: 360px" />
        <el-button type="primary" @click="loadAlarms">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>

    <el-table :data="alarms" border stripe v-loading="loading">
      <el-table-column prop="alarmNo" label="报警编号" width="160" />
      <el-table-column prop="storeName" label="所属粮库" width="160">
        <template #default="scope">
          {{ scope.row.storeName || '—' }}
        </template>
      </el-table-column>
      <el-table-column prop="alarmType" label="报警类型" width="110">
        <template #default="scope">
          <el-tag :type="getTypeTag(scope.row.alarmType)">{{ getTypeName(scope.row.alarmType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="content" label="报警内容" min-width="200" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.status === 0 ? 'danger' : scope.row.status === 1 ? 'warning' : 'success'">
            {{ getStatusName(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="报警时间" width="170">
        <template #default="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button size="small" type="primary" link @click="openHandle(scope.row)"
                     :disabled="scope.row.status === 2">处理</el-button>
          <el-button size="small" link @click="viewDetail(scope.row)">详情</el-button>
          <el-button size="small" type="danger" link @click="handleDeleteAlarm(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <MyPager :total="total" v-model:page="pageNum" v-model:size="pageSize" @change="loadAlarms" />

    <!-- 处理对话框 -->
    <el-dialog title="处理报警" v-model="handleVisible" width="500px">
      <el-form :model="handleForm" label-width="100px">
        <el-form-item label="报警内容">
          <div class="alarm-info">{{ handleForm.content }}</div>
        </el-form-item>
        <el-form-item label="处理人">
          <el-tag size="large" type="info">{{ currentUserName }}</el-tag>
          <span class="form-tip">（当前登录用户，无需填写）</span>
        </el-form-item>
        <el-form-item label="处理说明">
          <el-input v-model="handleForm.handleContent" type="textarea" :rows="3" placeholder="请输入处理说明" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="handleVisible = false">取消</el-button>
        <el-button type="primary" @click="submitHandle">确认处理</el-button>
      </template>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog title="报警详情" v-model="detailVisible" width="560px">
      <el-descriptions :column="2" border v-if="detailRow">
        <el-descriptions-item label="报警编号">{{ detailRow.alarmNo }}</el-descriptions-item>
        <el-descriptions-item label="所属粮库">{{ detailRow.storeName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="报警类型">{{ getTypeName(detailRow.alarmType) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ getStatusName(detailRow.status) }}</el-descriptions-item>
        <el-descriptions-item label="报警内容" :span="2">{{ detailRow.content }}</el-descriptions-item>
        <el-descriptions-item label="报警时间">{{ formatDateTime(detailRow.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="处理时间">{{ formatDateTime(detailRow.handleTime) || '—' }}</el-descriptions-item>
        <el-descriptions-item label="处理人">{{ detailRow.handler || '—' }}</el-descriptions-item>
        <el-descriptions-item label="处理说明">{{ detailRow.handleContent || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 自定义 toast -->
    <div v-if="toastShow" class="alarm-toast" :class="'toast-' + toastType">
      {{ toastMsg }}
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessageBox } from 'element-plus'
import { getAlarmListPage, getAlarmById, handleAlarm, deleteAlarm, getGrainStoreList } from '../api'
import { formatDateTime } from '../utils/format'
import MyPager from '../components/MyPager.vue'

// ---- 自定义 toast ----
const toastMsg = ref('')
const toastType = ref('success')
const toastShow = ref(false)
function toast(msg, type = 'success') {
  toastMsg.value = msg; toastType.value = type; toastShow.value = true
  clearTimeout(toast._t)
  toast._t = setTimeout(() => { toastShow.value = false }, 1800)
}

const loading = ref(false)
const alarms = ref([])
const stores = ref([])
const pageNum = ref(1)
const pageSize = ref(20)
const total = ref(0)

const query = reactive({
  storeId: null,
  alarmType: null,
  status: null,
  keyword: '',
  dateRange: null
})

onMounted(async () => {
  try {
    const res = await getGrainStoreList()
    stores.value = res.data || []
  } catch (e) { /* ignore */ }
  loadAlarms()
})

async function loadAlarms() {
  loading.value = true
  try {
    const params = { page: pageNum.value, size: pageSize.value }
    if (query.storeId) params.storeId = query.storeId
    if (query.alarmType) params.alarmType = query.alarmType
    if (query.status !== null && query.status !== undefined && query.status !== '') params.status = query.status
    if (query.keyword) params.keyword = query.keyword
    if (query.dateRange && query.dateRange.length === 2) {
      params.startTime = query.dateRange[0]
      params.endTime = query.dateRange[1]
    }
    const res = await getAlarmListPage(params)
    alarms.value = res.data?.list || []
    total.value = res.data?.total || 0
  } catch (e) {
    toast('加载报警失败', 'error')
  } finally {
    loading.value = false
  }
}

function resetQuery() {
  query.storeId = null
  query.alarmType = null
  query.status = null
  query.keyword = ''
  query.dateRange = null
  pageNum.value = 1
  loadAlarms()
}
function handleQuery() { pageNum.value = 1; loadAlarms() }

// ---- 处理报警 ----
const handleVisible = ref(false)
const handleForm = reactive({ id: null, content: '', handleContent: '' })

// 当前登录用户名（后端最终从 JWT 取，这里仅前端展示用）
const currentUserName = computed(() => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}')
    return u.realName || u.username || '当前用户'
  } catch { return '当前用户' }
})

function openHandle(row) {
  handleForm.id = row.id
  handleForm.content = row.content
  handleForm.handleContent = ''
  handleVisible.value = true
}

async function submitHandle() {
  if (!handleForm.handleContent.trim()) { toast('请输入处理说明', 'warning'); return }
  try {
    // 不传 handler — 后端从 JWT 取当前登录用户作为处理人
    await handleAlarm({
      id: handleForm.id,
      handleContent: handleForm.handleContent
    })
    handleVisible.value = false
    toast('处理成功')
    loadAlarms()
  } catch (e) {
    toast('处理失败', 'error')
  }
}

// ---- 详情 ----
const detailVisible = ref(false)
const detailRow = ref(null)
function viewDetail(row) {
  detailRow.value = row
  detailVisible.value = true
}

// ---- 删除 ----
async function handleDeleteAlarm(id) {
  try {
    await ElMessageBox.confirm('确定删除该报警记录？', '删除确认', { type: 'warning' })
  } catch {
    return  // 用户点取消
  }
  try {
    await deleteAlarm(id)
    toast('删除成功')
    loadAlarms()
  } catch (e) {
    toast('删除失败', 'error')
  }
}

// ---- 字典 ----
const getTypeName = (type) => ({ 1: '温度异常', 2: '湿度异常', 3: '设备故障', 4: '入侵报警' }[type] || '未知')
const getTypeTag   = (type) => ({ 1: 'danger', 2: 'warning', 3: 'info', 4: 'danger' }[type] || 'info')
const getStatusName = (status) => ({ 0: '待处理', 1: '处理中', 2: '已处理' }[status] || '未知')
</script>

<style scoped>
.page-container { padding: 16px; }
.toolbar { display: flex; justify-content: flex-end; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
.toolbar .filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; justify-content: flex-end; }
.alarm-info { padding: 8px 12px; background: #f5f7fa; border-radius: 4px; color: #606266; line-height: 1.6; }
.form-tip { color: #909399; font-size: 12px; margin-left: 8px; }

.alarm-toast {
  position: fixed; top: 40px; left: 50%; transform: translateX(-50%);
  padding: 10px 20px; border-radius: 4px; font-size: 14px;
  z-index: 9999; box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  animation: toast-in 0.2s ease;
}
@keyframes toast-in {
  from { opacity: 0; transform: translate(-50%, -10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
.toast-success { background: #f0f9eb; color: #67c23a; border: 1px solid #e1f3d8; }
.toast-error   { background: #fef0f0; color: #f56c6c; border: 1px solid #fde2e2; }
.toast-warning { background: #fdf6ec; color: #e6a23c; border: 1px solid #faecd8; }
</style>
